
import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding = 'md',
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'rounded-lg bg-white dark:bg-slate-800 transition-shadow';

    const variants = {
      default: 'border border-slate-200 dark:border-slate-700 shadow-sm',
      elevated: 'shadow-lg border border-slate-200 dark:border-slate-700',
      outlined: 'border border-slate-200 dark:border-slate-700',
    };

    const paddings = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    };

    return (
      <div
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          paddings[padding],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div
      className={cn(
        'mb-3 flex flex-wrap items-center gap-2',
        className
      )}
    >
      {children}
    </div>
  );
}

CardHeader.displayName = 'CardHeader';

export interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2 dark:border-slate-700',
        className
      )}
    >
      {children}
    </div>
  );
}

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter };
